import React, { useCallback, useMemo, useState } from "react";
import { AutocompleteInput } from "../../components/AutocompleteInput";
import { PageState } from "../../components/PageState";
import { CrudModal } from "../../components/CrudModal";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { useAuth } from "../../auth/AuthContext";
import { usePagedQuery } from "../../hooks/usePagedQuery";
import { TASK_PRIORITY_LABEL, TASK_STATUS_LABEL } from "../../lib/i18n/tasks";

type Tarefa = {
  id: number;
  titulo: string;
  descricao?: string | null;
  status: string;
  prioridade?: string | null;
  deadline?: string | null;
  criadoPorId: number;
  criadoPor?: { id: number; name: string; email: string } | null;
  atribuidaParaId?: number | null;
  atribuidaPara?: { id: number; name: string; email: string } | null;
};

type Option = { id: number; label: string };

const statusOptions = Object.entries(TASK_STATUS_LABEL).map(([value, label]) => ({
  value,
  label,
}));

const priorityOptions = Object.entries(TASK_PRIORITY_LABEL).map(([value, label]) => ({
  value,
  label,
}));

export const TasksPage: React.FC = () => {
  const { client, user } = useAuth();
  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [onlyOverdue, setOnlyOverdue] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const canManage = Boolean(user?.permissions?.includes("TASK_MANAGE"));

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit" | "view">("create");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingTask, setEditingTask] = useState<Tarefa | null>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [formStatus, setFormStatus] = useState("OPEN");
  const [formPriority, setFormPriority] = useState("MEDIUM");
  const [deadline, setDeadline] = useState("");
  const [assignee, setAssignee] = useState<Option | null>(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Tarefa | null>(null);

  const {
    rows,
    total,
    loading,
    error,
    page,
    take,
    setPage,
    q,
    setQ,
    reload,
  } = usePagedQuery<Tarefa>({
    endpoint: "/tasks",
    filters: {
      status: status || undefined,
      prioridade: priority || undefined,
      from: from || undefined,
      to: to || undefined,
      vencidas: onlyOverdue || undefined,
    },
  });

  const fetchAssignees = useCallback(
    async (text: string) => {
      const response = await client.get(
        `/tasks/assignees?q=${encodeURIComponent(text)}&take=10&skip=0`
      );
      if (!response.ok) {
        return [];
      }
      const payload = (await response.json()) as {
        data: Array<{ id: number; name: string; email: string }>;
      };
      return payload.data.map((item) => ({
        id: item.id,
        label: `${item.name} (${item.email})`,
      }));
    },
    [client]
  );

  const resetForm = () => {
    setEditingId(null);
    setEditingTask(null);
    setTitle("");
    setDescription("");
    setFormStatus("OPEN");
    setFormPriority("MEDIUM");
    setDeadline("");
    setAssignee(null);
  };

  const openModal = (mode: "create" | "edit" | "view", task?: Tarefa) => {
    setModalMode(mode);
    if (mode === "create") {
      resetForm();
    } else if (task) {
      setEditingId(task.id);
      setEditingTask(task);
      setTitle(task.titulo);
      setDescription(task.descricao ?? "");
      setFormStatus(task.status ?? "OPEN");
      setFormPriority(task.prioridade ?? "MEDIUM");
      setDeadline(task.deadline ? task.deadline.slice(0, 10) : "");
      setAssignee(
        task.atribuidaPara
          ? {
              id: task.atribuidaPara.id,
              label: `${task.atribuidaPara.name} (${task.atribuidaPara.email})`,
            }
          : null
      );
    }
    setModalOpen(true);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setMessage(null);
    const payload = {
      titulo: title,
      descricao: description || null,
      status: formStatus,
      prioridade: formPriority,
      deadline: deadline || null,
      atribuidaParaId: assignee?.id ?? null,
    };
    const response = editingId
      ? await client.put(`/tasks/${editingId}`, payload)
      : await client.post("/tasks", payload);
    if (!response.ok) {
      setMessage("Falha ao salvar tarefa.");
      return;
    }
    setModalOpen(false);
    resetForm();
    await reload();
  };

  const handleDelete = async () => {
    const task = pendingDelete;
    if (!task) {
      return;
    }
    const response = await client.del(`/tasks/${task.id}`);
    if (!response.ok) {
      setMessage("Falha ao remover.");
      return;
    }
    setConfirmOpen(false);
    setPendingDelete(null);
    await reload();
  };

  const handleComplete = async (task: Tarefa) => {
    const response = await client.patch(`/tasks/${task.id}/complete`, {});
    if (!response.ok) {
      setMessage("Falha ao concluir.");
      return;
    }
    await reload();
  };

  const handleCancel = async (task: Tarefa) => {
    const response = await client.put(`/tasks/${task.id}`, { status: "CANCELED" });
    if (!response.ok) {
      setMessage("Falha ao cancelar.");
      return;
    }
    await reload();
  };

  const paginationText = useMemo(() => {
    if (total === 0) {
      return "0 resultados";
    }
    const start = (page - 1) * take + 1;
    const end = Math.min(page * take, total);
    return `${start} - ${end} de ${total}`;
  }, [page, take, total]);

  const readOnly = modalMode === "view" || !canManage;

  return (
    <>
      <div className="container py-4">
        <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
          <h1 className="mb-0">Tarefas</h1>
          {canManage && (
            <button className="btn btn-primary" type="button" onClick={() => openModal("create")}> 
              Nova tarefa
            </button>
          )}
        </div>

        <div className="card shadow-sm mb-3">
          <div className="card-body">
            <div className="row g-3 align-items-end">
              <div className="col-md-2">
                <label className="form-label">Status</label>
                <select
                  className="form-select"
                  value={status}
                  onChange={(event) => setStatus(event.target.value)}
                >
                  <option value="">Todos</option>
                  {statusOptions.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label">Prioridade</label>
                <select
                  className="form-select"
                  value={priority}
                  onChange={(event) => setPriority(event.target.value)}
                >
                  <option value="">Todas</option>
                  {priorityOptions.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label">De</label>
                <input
                  className="form-control"
                  type="date"
                  value={from}
                  onChange={(event) => setFrom(event.target.value)}
                />
              </div>
              <div className="col-md-2">
                <label className="form-label">Ate</label>
                <input
                  className="form-control"
                  type="date"
                  value={to}
                  onChange={(event) => setTo(event.target.value)}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Busca</label>
                <input
                  className="form-control"
                  placeholder="Buscar tarefa"
                  value={q}
                  onChange={(event) => setQ(event.target.value)}
                />
              </div>
              <div className="col-md-3">
                <div className="form-check mt-4">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={onlyOverdue}
                    onChange={(event) => setOnlyOverdue(event.target.checked)}
                    id="onlyOverdue"
                  />
                  <label className="form-check-label" htmlFor="onlyOverdue">
                    Somente vencidas
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        {message && <div className="alert alert-danger">{message}</div>}

        <div className="card shadow-sm">
          <div className="card-body">
            <PageState loading={loading} error={error}>
              <div className="table-responsive">
                <table className="table table-sm align-middle">
                  <thead>
                    <tr>
                      <th>Titulo</th>
                      <th>Responsavel</th>
                      <th>Prioridade</th>
                      <th>Deadline</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((task) => (
                      <tr key={task.id}>
                        <td>{task.titulo}</td>
                        <td>{task.atribuidaPara ? task.atribuidaPara.name : "-"}</td>
                        <td>
                          {task.prioridade
                            ? TASK_PRIORITY_LABEL[task.prioridade] ?? task.prioridade
                            : "-"}
                        </td>
                        <td>{task.deadline ? task.deadline.slice(0, 10) : "-"}</td>
                        <td>
                          <span className="badge text-bg-light">
                            {task.status ? TASK_STATUS_LABEL[task.status] ?? task.status : "-"}
                          </span>
                        </td>
                        <td className="text-end">
                          <div className="btn-group">
                            {canManage ? (
                              <>
                                <button
                                  className="btn btn-outline-secondary btn-sm"
                                  onClick={() => openModal("edit", task)}
                                >
                                  Editar
                                </button>
                                <button
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => {
                                    setPendingDelete(task);
                                    setConfirmOpen(true);
                                  }}
                                >
                                  Remover
                                </button>
                                {task.status !== "DONE" && task.status !== "CANCELED" && (
                                  <button
                                    className="btn btn-outline-success btn-sm"
                                    onClick={() => handleComplete(task)}
                                  >
                                    Concluir
                                  </button>
                                )}
                                {task.status !== "CANCELED" && (
                                  <button
                                    className="btn btn-outline-warning btn-sm"
                                    onClick={() => handleCancel(task)}
                                  >
                                    Cancelar
                                  </button>
                                )}
                              </>
                            ) : (
                              <button
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() => openModal("view", task)}
                              >
                                Ver
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {rows.length === 0 && (
                      <tr>
                        <td colSpan={6} className="text-center text-muted">
                          Nenhuma tarefa encontrada.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </PageState>
            <div className="d-flex justify-content-between align-items-center">
              <small className="text-muted">{paginationText}</small>
              <div className="btn-group">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  disabled={page <= 1}
                  onClick={() => setPage(Math.max(1, page - 1))}
                >
                  Anterior
                </button>
                <button
                  className="btn btn-outline-secondary btn-sm"
                  disabled={page * take >= total}
                  onClick={() => setPage(page + 1)}
                >
                  Proximo
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CrudModal
        title={
          modalMode === "create"
            ? "Nova tarefa"
            : modalMode === "edit"
              ? "Editar tarefa"
              : "Detalhes da tarefa"
        }
        open={modalOpen}
        mode={modalMode}
        canWrite={canManage}
        onClose={() => setModalOpen(false)}
        formId="task-form"
      >
        <form onSubmit={handleSubmit} id="task-form">
          <div className="mb-3">
            <label className="form-label">Criado por</label>
            <input
              className="form-control"
              value={
                editingId
                  ? editingTask?.criadoPor
                    ? `${editingTask.criadoPor.name} (${editingTask.criadoPor.email})`
                    : editingTask?.criadoPorId
                      ? `Usuario #${editingTask.criadoPorId}`
                      : "-"
                  : user?.name ?? "-"
              }
              disabled
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Titulo</label>
            <input
              className="form-control"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              required
              disabled={readOnly}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Descricao</label>
            <textarea
              className="form-control"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              rows={3}
              disabled={readOnly}
            />
          </div>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={formStatus}
                onChange={(event) => setFormStatus(event.target.value)}
                disabled={readOnly}
              >
                {statusOptions.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label">Prioridade</label>
              <select
                className="form-select"
                value={formPriority}
                onChange={(event) => setFormPriority(event.target.value)}
                disabled={readOnly}
              >
                {priorityOptions.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label">Deadline</label>
              <input
                className="form-control"
                type="date"
                value={deadline}
                onChange={(event) => setDeadline(event.target.value)}
                disabled={readOnly}
              />
            </div>
          </div>
          <div className="mt-3">
            <AutocompleteInput
              label="Responsavel"
              placeholder="Buscar usuario"
              value={null}
              selectedLabel={assignee?.label ?? ""}
              fetchOptions={fetchAssignees}
              disabled={readOnly}
              onSelect={(option) => {
                if (!option) {
                  setAssignee(null);
                  return;
                }
                setAssignee(option);
              }}
            />
            {assignee && !readOnly && (
              <button
                type="button"
                className="btn btn-link p-0 mt-2"
                onClick={() => setAssignee(null)}
              >
                Remover responsavel
              </button>
            )}
            {!assignee && <span className="text-muted small">Nenhum responsavel.</span>}
          </div>
        </form>
      </CrudModal>

      <ConfirmDialog
        open={confirmOpen}
        title="Confirmar remocao"
        description="Deseja remover esta tarefa?"
        confirmLabel="Remover"
        cancelLabel="Cancelar"
        danger
        onCancel={() => {
          setConfirmOpen(false);
          setPendingDelete(null);
        }}
        onConfirm={handleDelete}
      />
    </>
  );
};
